import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { Button, Checkbox, Divider, Popover, Radio, Switch } from 'antd';
import SearchBox from 'components/molecules/SearchBox';
import FieldsIcon from 'assets/svg/Fields';
import FilterIcon from 'assets/svg/Filter';
import NewGlobalFilter from 'components/molecules/common/NewGlobalFilter/NewGlobalFilter';
import FilterRibbon from 'components/molecules/PricingSimulator/FilterRibbon/FilterRibbon';
import { tpoGenUrls } from 'config/urls';
import { transformFiltersV2 } from 'utils/helperMethods';
import { useTpoGenConfig } from '../context/CustomerConfigContext';
import type { PlanIndexFilterState } from 'utils/planIndexFilterParams';

const FieldMultiSelect = ({
    fieldOptions,
    selectedFields,
    setSelectedFields,
}: {
    fieldOptions: { key: string; label: string }[];
    selectedFields: string[];
    setSelectedFields: Dispatch<SetStateAction<string[]>>;
}) => {
    const tpoGenConfig = useTpoGenConfig();
    const realFields = fieldOptions.filter((f) => f.key !== 'all');
    const allSelected = realFields.length > 0 && realFields.every((f) => selectedFields.includes(f.key));
    const someSelected = !allSelected && realFields.some((f) => selectedFields.includes(f.key));

    const handleSelectAll = () => {
        if (allSelected) {
            setSelectedFields([]);
        } else {
            setSelectedFields(realFields.map((f) => f.key));
        }
    };

    const handleFieldToggle = (field: string) => {
        if (selectedFields.includes(field)) {
            setSelectedFields((prev) => prev.filter((f) => f !== field));
        } else {
            setSelectedFields((prev) => [...prev, field]);
        }
    };

    return (
        <div className="py-3">
            <p className="text-[#807E86] text-xs">{tpoGenConfig.labels.planIndex.selectFields}</p>
            <Divider className="!my-[0.3125rem]" />
            <div className="flex flex-col items-start">
                <div className="px-3 py-[0.3125rem]">
                    <Checkbox checked={allSelected} indeterminate={someSelected} onChange={handleSelectAll}>
                        <p className="text-sm">Select All</p>
                    </Checkbox>
                </div>
                {realFields.map((_field) => (
                    <div className="px-3 py-[0.3125rem]" key={_field.key}>
                        <Checkbox
                            checked={selectedFields.includes(_field.key)}
                            onChange={() => handleFieldToggle(_field.key)}
                        >
                            <p className="text-sm">{_field.label}</p>
                        </Checkbox>
                    </div>
                ))}
            </div>
        </div>
    );
};

interface PlanIndexToolbarProps {
    searchKeyword: string;
    setSearchKeyword: (value: string) => void;
    showMyPlans: boolean;
    setShowMyPlans: (value: boolean) => void;
    selectedColumns: string[];
    setSelectedColumns: (value: string[]) => void;
    allCheckedAttribute: PlanIndexFilterState['allCheckedAttribute'];
    allSelectedValueForAllAttribute: PlanIndexFilterState['allSelectedValueForAllAttribute'];
    applyFilterObject: PlanIndexFilterState['applyFilterObject'];
    setAppliedFilters: (result: PlanIndexFilterState) => void;
    resetPlanIndexFilters: () => void;
    onCreateNewPlanClick: () => void;
}

const PlanIndexToolbar = ({
    searchKeyword,
    setSearchKeyword,
    showMyPlans,
    setShowMyPlans,
    selectedColumns,
    setSelectedColumns,
    allCheckedAttribute,
    allSelectedValueForAllAttribute,
    applyFilterObject,
    setAppliedFilters,
    resetPlanIndexFilters,
    onCreateNewPlanClick,
}: PlanIndexToolbarProps) => {
    const tpoGenConfig = useTpoGenConfig();
    const { planIndexColumns, planIndexFilterKeys, filterDimensionInfo } = tpoGenConfig;
    const [search, setSearch] = useState<string>(() => searchKeyword);
    const [selectedFields, setSelectedFields] = useState<string[]>(() => selectedColumns);
    const selectColumnFields = [
        { key: 'all', label: 'Select All' },
        ...planIndexColumns.map((c) => ({ key: c.key, label: c.label })),
    ];

    const [selectedFilter, setSelectedFilter] = useState<any>(null);
    const [tempApplyFilterObject, setTempApplyFilterObject] = useState<any>({});

    const onFilterSaveClickHandler = useCallback(
        (resultObj: PlanIndexFilterState) => {
            setAppliedFilters(resultObj);
        },
        [setAppliedFilters],
    );

    const filterApiVariables = useMemo(() => {
        if (selectedFilter?.value) {
            const updatedFilters = transformFiltersV2(tempApplyFilterObject, planIndexFilterKeys, selectedFilter.value);
            return {
                attribute_type: selectedFilter.value,
                limit: 50,
                filters: updatedFilters,
            };
        }
    }, [selectedFilter, tempApplyFilterObject, planIndexFilterKeys]);

    return (
        <>
            <div className="flex flex-row justify-between flex-wrap">
                <div className="flex items-center">
                    <div className="flex flex-col gap-y-[0.5rem]">
                        <div className="flex gap-x-[0.75rem]">
                            <p className="text-[#18181B] font-semibold text-base">
                                {tpoGenConfig.labels.planIndex.sectionTitle}
                            </p>
                            {/* Tabs Section */}
                            <Radio.Group defaultValue={'plans'}>
                                <Radio.Button value="plans">{tpoGenConfig.labels.planIndex.tabs.plans}</Radio.Button>
                                <Radio.Button value="comparison-reports" disabled>
                                    {tpoGenConfig.labels.planIndex.tabs.comparisonReports}
                                </Radio.Button>
                            </Radio.Group>
                        </div>
                        <div>
                            <p className="text-[#A49FA9] text-sm font-normal">
                                {tpoGenConfig.labels.planIndex.description}
                            </p>
                        </div>
                    </div>
                </div>
                {/* Right Side of the Toolbar */}
                <div className="flex gap-x-[0.6rem] items-center">
                    <SearchBox
                        onSearch={(val: string) => setSearchKeyword(val)}
                        search={search}
                        setSearch={setSearch}
                    />
                    <div className="flex gap-[0.75rem] items-center">
                        <p className="text-[#807E86] text-xs">{tpoGenConfig.labels.planIndex.myPlan}</p>
                        <Switch checked={showMyPlans} onChange={(val: boolean) => setShowMyPlans(val)} />
                    </div>
                    <NewGlobalFilter
                        availableFilters={planIndexFilterKeys}
                        allCheckedAttribute={allCheckedAttribute}
                        allSelectedValueForAllAttribute={allSelectedValueForAllAttribute}
                        applyFilterObject={applyFilterObject}
                        resetFilterObject={resetPlanIndexFilters}
                        onSubmitHandler={onFilterSaveClickHandler}
                        url={tpoGenUrls.getPlanIndexFilterAttributes}
                        selectedFilter={selectedFilter}
                        setSelectedFilter={setSelectedFilter}
                        tempApplyFilterObject={tempApplyFilterObject}
                        setTempApplyFilterObject={setTempApplyFilterObject}
                        apiVariables={filterApiVariables}
                        filterDimensionInfo={filterDimensionInfo}
                        showAttributePanelToLeft
                        filterIcon={<FilterIcon />}
                    />
                    <Popover
                        trigger={'click'}
                        showArrow={false}
                        placement="bottomRight"
                        content={
                            <FieldMultiSelect
                                fieldOptions={selectColumnFields}
                                selectedFields={selectedFields}
                                setSelectedFields={setSelectedFields}
                            />
                        }
                        align={{ offset: [0, -8] }}
                        onOpenChange={(open) => {
                            if (!open) {
                                setSelectedColumns(
                                    selectColumnFields
                                        .filter((col) => col.key !== 'all' && selectedFields.includes(col.key))
                                        .map((col) => col.key),
                                );
                            }
                        }}
                    >
                        <Button>
                            <div className="flex flex-row gap-x-[0.5rem] items-center">
                                <FieldsIcon />
                                <p>{tpoGenConfig.labels.planIndex.fields}</p>
                            </div>
                        </Button>
                    </Popover>
                    <Button type="primary" onClick={onCreateNewPlanClick}>
                        {tpoGenConfig.labels.planIndex.createNewPlan}
                    </Button>
                </div>
                <Divider className="!my-3" />
            </div>

            <div className="mb-3">
                <FilterRibbon
                    allCheckedAttribute={allCheckedAttribute}
                    allSelectedValueForAllAttribute={allSelectedValueForAllAttribute}
                    applyFilterObject={applyFilterObject}
                    resetFilterObject={resetPlanIndexFilters}
                    onSubmitHandler={onFilterSaveClickHandler}
                />
            </div>
        </>
    );
};

export default PlanIndexToolbar;
